import { useState, useEffect, useCallback } from 'react';
import type { RiderDetailInfo, RiderAccountInfo } from '../types/riderInfoDetail.types';
import type { RiderApiItem } from '../types/riderInfoDetail.api.types';
import { riderInfoDetailApi } from '../api/riderInfoDetailApi';
import {
    formatDate,
    formatPhoneNumber,
    formatBirthDate,
    formatResidentNumber,
    formatContractStatus,
} from '@/shared/utils/formatter';

export const useRiderBasicInfo = (riderId?: string) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [riderInfo, setRiderInfo] = useState<RiderDetailInfo>({
        name: '',
        phone: '',
        id: riderId || '',
        birthDate: '',
        contractStatus: '',
        deliveryMethod: '',
        residentNumber: '',
        registrationDate: '',
        contractEndDate: '',
    });
    const [suppId, setSuppId] = useState('');
    const [initialAccountInfo, setInitialAccountInfo] = useState<RiderAccountInfo | undefined>(undefined);
    const [initialRiderTypeCode, setInitialRiderTypeCode] = useState('');

    const mapApiResponseToRiderInfo = useCallback((apiData: RiderApiItem): RiderDetailInfo => {
        return {
            name: apiData.name || '',
            phone: formatPhoneNumber(apiData.phoneNumber || ''),
            id: apiData.riderId || '',
            birthDate: formatBirthDate(apiData.birthDate || ''),
            contractStatus: formatContractStatus(
                apiData.status?.contractStatusName ||
                apiData.contractStatusName ||
                apiData.contStatus ||
                '',
            ),
            deliveryMethod: apiData.deliveryMethName || '',
            residentNumber: formatResidentNumber(apiData.registNum || ''),
            registrationDate: formatDate(apiData.riderInsDate || apiData.insDate || ''),
            contractEndDate: formatDate(apiData.contEdDate || apiData.updDate || ''),
            supplierName: apiData.centerName || apiData.suppName || '',
            supplierId: apiData.suppId || '',
            riderTypeCode: apiData.riderType || apiData.typeCode || '',
            riderTypeName: apiData.riderTypeName || apiData.typeName || '',
            preAcctYn: apiData.preAcctYn || '',
            acctApplyDay: apiData.acctApplyDay || '',
            acctApplyType: apiData.acctApplyType || '',
            acctApplyTypeName: apiData.acctApplyTypeName || '',
            nextAcctApplyDay: apiData.nextAcctApplyDay || '',
            nextAcctApplyType: apiData.nextAcctApplyType || '',
            nextAcctApplyTypeName: apiData.nextAcctApplyTypeName || '',
        };
    }, []);

    const fetchRiderDetail = useCallback(async () => {
        if (!riderId) {
            setError('라이더 ID가 없습니다.');
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            setError(null);

            const apiData = await riderInfoDetailApi.getRiderDetail(riderId);
            setRiderInfo(mapApiResponseToRiderInfo(apiData));

            const currentSuppId = apiData.suppId || '';
            setSuppId(currentSuppId);

            if (apiData.accountNum || apiData.bankCode) {
                setInitialAccountInfo({
                    bank: apiData.bankCode || '',
                    accountNumber: apiData.accountNum || '',
                    accountHolder: apiData.accName || '',
                });
            }

            const riderTypeCode = apiData.riderType || apiData.typeCode || '';
            setInitialRiderTypeCode(riderTypeCode ? riderTypeCode.toString() : '');
        } catch (err: unknown) {
            setError(
                err instanceof Error ? err.message : '라이더 상세 정보를 불러오는데 실패했습니다.',
            );
        } finally {
            setLoading(false);
        }
    }, [riderId, mapApiResponseToRiderInfo]);

    useEffect(() => {
        fetchRiderDetail();
    }, [fetchRiderDetail]);

    return { loading, error, riderInfo, suppId, initialAccountInfo, initialRiderTypeCode, fetchRiderDetail };
};
